import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import {
  type FileKind,
  type FileUploadItem,
  formatSize,
  isPdfFile,
} from "../types";
import { FileDropZone } from "./FileDropZone";

interface UploadStepProps {
  files: FileUploadItem[];
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (id: string) => void;
  onOpenRegionSelector: (id: string) => void;
  onSetFileKind: (id: string, kind: FileKind) => void;
  onProcess: () => void;
  /** Fires when there are no pending files but already-processed data exists,
   *  so the user can move forward to Review without re-processing. */
  onContinueToReview?: () => void;
  processing: boolean;
  hasReviewData: boolean;
}

const MAX_BYTES = 100 * 1024 * 1024;

export function UploadStep({
  files,
  onAddFiles,
  onRemoveFile,
  onOpenRegionSelector,
  onSetFileKind,
  onProcess,
  onContinueToReview,
  processing,
  hasReviewData,
}: UploadStepProps) {
  const pendingCount = files.filter((f) => f.status !== "Completed").length;
  // If everything is already processed AND we have parsed data, the primary
  // action should just navigate forward — no point re-running extraction.
  const goingForward = pendingCount === 0 && hasReviewData;
  const handleSelected = (selected: File[]) => {
    const ok: File[] = [];
    const existingKeys = new Set(
      files.map((f) => `${f.file.name}|${f.file.size}`),
    );
    for (const f of selected) {
      if (existingKeys.has(`${f.name}|${f.size}`)) {
        toast.warning(`File '${f.name}' already uploaded`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`Failed to read file '${f.name}': exceeds 100MB`);
        continue;
      }
      ok.push(f);
    }
    if (ok.length) onAddFiles(ok);
  };

  return (
    <div>
      <div className="max-w-2xl">
        <div className="label mb-2">New AI1</div>
        <h1
          className="text-[clamp(2.25rem,4.5vw,3.25rem)] font-medium tracking-tight text-ink"
          style={{ fontVariationSettings: '"wdth" 90, "opsz" 72' }}
        >
          Upload your documents
        </h1>
        <p className="mt-4 max-w-lg text-[1.05rem] leading-relaxed text-ink-soft">
          PDF, Excel, CSV, or BAX files containing DOB objections. Drag in
          or click to browse.
        </p>
      </div>

      <div className="mt-8">
        <FileDropZone onFiles={handleSelected} />
      </div>

      {files.length > 0 && (
        <div className="mt-8 overflow-hidden rounded-lg border border-rule bg-surface shadow-soft">
          <div className="flex items-baseline justify-between border-b border-rule px-6 py-4">
            <div className="text-[0.95rem] font-semibold text-ink">
              Selected files
              <span className="ml-2 text-ink-muted">({files.length})</span>
            </div>
            <div className="text-[0.78rem] text-ink-muted">
              Click × to remove
            </div>
          </div>
          <ul className="divide-y divide-rule">
            {files.map((f) => (
              <FileRow
                key={f.id}
                file={f}
                onRemove={() => onRemoveFile(f.id)}
                onOpenRegionSelector={() => onOpenRegionSelector(f.id)}
                onSetKind={(kind) => onSetFileKind(f.id, kind)}
              />
            ))}
          </ul>
        </div>
      )}

      <div className="mt-8 flex items-center gap-4">
        <Button
          intent="primary"
          size="lg"
          disabled={files.length === 0 || processing}
          onClick={() => {
            if (goingForward && onContinueToReview) {
              onContinueToReview();
            } else {
              onProcess();
            }
          }}
        >
          {processing
            ? "Processing…"
            : goingForward
              ? "Continue to review"
              : pendingCount > 0 && pendingCount < files.length
                ? `Process ${pendingCount} new file${pendingCount === 1 ? "" : "s"}`
                : "Process files"}
          <ArrowRight />
        </Button>
        {files.length === 0 && (
          <span className="text-[0.88rem] text-ink-muted">
            Pick at least one file
          </span>
        )}
        {goingForward && (
          <span className="text-[0.88rem] text-ink-muted">
            All files already processed
          </span>
        )}
      </div>
    </div>
  );
}

function FileRow({
  file,
  onRemove,
  onOpenRegionSelector,
  onSetKind,
}: {
  file: FileUploadItem;
  onRemove: () => void;
  onOpenRegionSelector: () => void;
  onSetKind: (kind: FileKind) => void;
}) {
  const isPdf = isPdfFile(file.file.name);
  return (
    <li className="flex items-center gap-4 px-6 py-3.5">
      <FileTypeChip filename={file.file.name} />
      <div className="flex-1 min-w-0">
        <div className="truncate text-[0.95rem] text-ink">{file.file.name}</div>
        <div className="text-[0.8rem] text-ink-muted">
          {formatSize(file.file.size)}
        </div>
      </div>

      <FileKindDropdown value={file.kind} onChange={onSetKind} />

      {isPdf && (() => {
        const definedCount = Object.keys(file.tableRegions?.regions ?? {}).length;
        const hasRegions = definedCount > 0;
        return (
          <button
            type="button"
            onClick={onOpenRegionSelector}
            title={
              hasRegions
                ? `Regions defined on ${definedCount} page(s) - click to modify`
                : "Select table region for extraction"
            }
            className={cn(
              "rounded-md px-3 py-1.5 text-[0.82rem] font-medium transition-colors",
              hasRegions
                ? "bg-sage-soft text-sage-ink hover:bg-sage/20"
                : "bg-surface-2 text-ink-soft hover:bg-primary-soft hover:text-primary-ink",
            )}
          >
            {hasRegions ? `✓ Regions · ${definedCount}` : "Select region"}
          </button>
        );
      })()}

      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${file.file.name}`}
        className="flex h-8 w-8 items-center justify-center rounded-full text-ink-faint hover:bg-danger-soft hover:text-danger"
      >
        ×
      </button>
    </li>
  );
}

function FileTypeChip({ filename }: { filename: string }) {
  const lower = filename.toLowerCase();
  const map: Record<string, { label: string; cls: string }> = {
    pdf: { label: "PDF", cls: "bg-primary-soft text-primary-ink" },
    csv: { label: "CSV", cls: "bg-sage-soft text-sage-ink" },
    bax: { label: "BAX", cls: "bg-lilac-soft text-lilac-ink" },
    xls: { label: "XLS", cls: "bg-butter-soft text-butter-ink" },
  };
  const key = lower.endsWith(".pdf")
    ? "pdf"
    : lower.endsWith(".csv")
      ? "csv"
      : lower.endsWith(".bax")
        ? "bax"
        : "xls";
  const { label, cls } = map[key];
  return (
    <span
      className={cn(
        "inline-flex h-10 w-12 shrink-0 items-center justify-center rounded-md text-[0.72rem] font-semibold tracking-wider",
        cls,
      )}
    >
      {label}
    </span>
  );
}

const FILE_KIND_OPTIONS: { value: FileKind; label: string; hint: string }[] = [
  { value: "Regular", label: "Regular", hint: "Default — no EN1 assignee" },
  { value: "EN", label: "EN", hint: "Energy — adds EN1 assignee in Review" },
];

function FileKindDropdown({
  value,
  onChange,
}: {
  value: FileKind;
  onChange: (kind: FileKind) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLUListElement>(null);
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null);

  // Position the popover relative to the trigger; portal-rendered so the
  // card's overflow-hidden doesn't clip it. Re-measure on scroll/resize.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      setCoords({
        top: r.bottom + 6,
        right: window.innerWidth - r.right,
      });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isEn = value === "EN";

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="File type — EN enables the EN1 assignee in Review"
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[0.82rem] font-medium transition-colors focus:outline-none focus:shadow-ring",
          isEn
            ? "border-butter/50 bg-butter-soft text-butter-ink hover:bg-butter/25"
            : "border-rule bg-surface text-ink-soft hover:border-primary/40 hover:text-ink",
        )}
      >
        {value}
        <Chevron open={open} />
      </button>
      {open && coords &&
        createPortal(
          <ul
            ref={popoverRef}
            role="listbox"
            style={{ position: "fixed", top: coords.top, right: coords.right }}
            className="z-50 min-w-[12rem] overflow-hidden rounded-lg border border-rule bg-surface py-1 shadow-soft"
          >
            {FILE_KIND_OPTIONS.map((opt) => {
              const selected = opt.value === value;
              return (
                <li key={opt.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={cn(
                      "flex w-full items-start gap-2 px-3 py-1.5 text-left transition-colors",
                      selected
                        ? "bg-primary-soft text-primary-ink"
                        : "text-ink-soft hover:bg-canvas-2 hover:text-ink",
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full",
                        selected ? "bg-primary" : "bg-rule",
                      )}
                    />
                    <span className="flex flex-col">
                      <span className="text-[0.85rem] font-medium leading-tight">
                        {opt.label}
                      </span>
                      <span className="text-[0.72rem] text-ink-muted leading-tight">
                        {opt.hint}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body,
        )}
    </>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      className={cn("transition-transform", open && "rotate-180")}
      aria-hidden
    >
      <path
        d="M2 4l3 3 3-3"
        stroke="currentColor"
        strokeWidth="1.6"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
